import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { MapPin, Mail, Phone, Clock } from "lucide-react"
import { SPACING } from "@/constants/layout"
import { COMPANY_CONTACT } from "@/constants/contact"
import { useContactForm } from "@/hooks/useContactForm"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { contactSchema, type ContactFormData } from "../lib/validations/contact"

const inquiryTypes = [
    "Investment Services",
    "Software",
    "Consulting",
    "Research Access",
    "Community Programs",
    "Career Opportunities",
    "Media Inquiry",
    "Other"
]

export function ContactPage() {
    const { handleSubmit: handleFormSubmit, isLoading, error, success } = useContactForm()
    const form = useForm<ContactFormData>({
        resolver: zodResolver(contactSchema),
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            inquiryType: "",
            message: ""
        }
    })

    const onSubmit = form.handleSubmit(async (data) => {
        await handleFormSubmit(data)
    })

    return (
        <div className={SPACING.page}>
            {/* Hero Section */}
            <div className="container mx-auto px-4">
                <div className="max-w-3xl mx-auto text-center">
                    <h1 className="text-4xl font-bold tracking-tight mb-6">
                        Get in Touch
                    </h1>
                    <p className="text-lg text-neutral-600 dark:text-neutral-400">
                        Have questions? We're here to help and would love to hear from you.
                    </p>
                </div>
            </div>

            {/* Contact Form Section */}
            <div className="container mx-auto px-4">
                <div className="grid lg:grid-cols-2 gap-12">
                    <Card variant="default">
                        <CardHeader>
                            <CardTitle>Send us a Message</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={onSubmit} className={SPACING.content}>
                                {error && (
                                    <Alert variant="destructive">
                                        <AlertDescription>{error}</AlertDescription>
                                    </Alert>
                                )}
                                {success && (
                                    <Alert>
                                        <AlertDescription>Message sent successfully!</AlertDescription>
                                    </Alert>
                                )}
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="firstName">First Name</Label>
                                        <Input
                                            {...form.register("firstName")}
                                            id="firstName"
                                            name="firstName"
                                            autoComplete="given-name"
                                            placeholder="John"
                                        />
                                        {form.formState.errors.firstName && (
                                            <p className="text-sm text-red-500">
                                                {form.formState.errors.firstName.message}
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="lastName">Last Name</Label>
                                        <Input
                                            {...form.register("lastName")}
                                            id="lastName"
                                            name="lastName"
                                            autoComplete="family-name"
                                            placeholder="Doe"
                                        />
                                        {form.formState.errors.lastName && (
                                            <p className="text-sm text-red-500">
                                                {form.formState.errors.lastName.message}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        {...form.register("email")}
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        placeholder="john@example.com"
                                    />
                                    {form.formState.errors.email && (
                                        <p className="text-sm text-red-500">
                                            {form.formState.errors.email.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="inquiryType">Inquiry Type</Label>
                                    <Select
                                        name="inquiryType"
                                        onValueChange={(value) => form.setValue("inquiryType", value)}
                                    >
                                        <SelectTrigger
                                            id="inquiryType"
                                            name="inquiryType"
                                        >
                                            <SelectValue
                                                placeholder="Select inquiry type"
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {inquiryTypes.map((type) => (
                                                <SelectItem
                                                    key={type}
                                                    value={type.toLowerCase()}
                                                    id={`inquiry-${type.toLowerCase()}`}
                                                >
                                                    {type}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {form.formState.errors.inquiryType && (
                                        <p className="text-sm text-red-500">
                                            {form.formState.errors.inquiryType.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="message">Message</Label>
                                    <Textarea
                                        {...form.register("message")}
                                        id="message"
                                        name="message"
                                        placeholder="Your message here..."
                                    />
                                    {form.formState.errors.message && (
                                        <p className="text-sm text-red-500">
                                            {form.formState.errors.message.message}
                                        </p>
                                    )}
                                </div>

                                <Button type="submit" disabled={isLoading}>
                                    {isLoading ? "Sending..." : "Send"}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>

                    <div className={SPACING.content}>
                        <Card variant="default">
                            <CardHeader>
                                <CardTitle>Support Hours</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-start space-x-4">
                                    <Clock className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                                    <div>
                                        <p className="font-medium">Monday - Friday</p>
                                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                            9:00 AM - 6:00 PM (Local Time)
                                        </p>
                                    </div>
                                </div>
                                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                    Our team is available during business hours to assist you.
                                    For urgent matters outside these hours, please email us.
                                </p>
                            </CardContent>
                        </Card>

                        {COMPANY_CONTACT.offices.map((office) => (
                            <Card key={office.city} variant="default">
                                <CardHeader>
                                    <CardTitle>{office.city} Office</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-start space-x-4">
                                        <MapPin className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                            {office.address}
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-4">
                                        <Phone className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                            {office.phone}
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-4">
                                        <Mail className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                            {office.email}
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
} 